import { parseISO } from "date-fns";
import { format, toZonedTime } from "date-fns-tz";
import type { NarrativeClusterRowData } from "@/components/discovery/narrative-clusters-table/NarrativeClustersTableStates";
import { getEnumLabel } from "@/components/news-feed/utils/filter-lookups";
import type { SocialMetrics } from "@/components/ui/molecules/feed-article-tooltips/SocialEngagementTooltip";
import type {
  GetFeedArticlesTableDataArticle,
  GetNarrativeClustersTableRow,
} from "@/data-access/discovery-view/queries/discovery-view-queries";
import type useFilterDictionary from "@/store/filter-dictionary/filter-dictionary.slice";

/**
 * Formats a date string to MM/DD/YYYY format
 */
const formatDateToMMDDYYYY = (
  dateString: string | null | undefined,
): string => {
  if (!dateString) return "";

  try {
    const parsedDate = parseISO(dateString);
    const utcDate = toZonedTime(parsedDate, "UTC");
    return format(utcDate, "MM/dd/yyyy", {
      timeZone: "UTC",
    });
  } catch {
    return "";
  }
};

/**
 * Transformed article with display labels for enum fields
 */
export type TransformedFeedArticle = GetFeedArticlesTableDataArticle & {
  contentCategoryLabel: string;
  formattedDatePublished: string;
  languageLabel: string;
  prominenceLabel: string;
  publisherCountryLabel: string;
  publisherRegionLabel: string;
  publisherTierLabel: string;
  sentimentPolarityLabel: string;
};

/**
 * Transforms a single article with enum labels
 * This will be used by the SSRM datasource to transform articles
 */
export const transformArticleWithLabels = (
  article: GetFeedArticlesTableDataArticle,
  dictionary: ReturnType<typeof useFilterDictionary.getState>,
): TransformedFeedArticle => {
  const contentCategoryLabel = article.contentCategory
    ? getEnumLabel("CATEGORY", article.contentCategory, dictionary)
    : "";

  // Prominence label: show prominence enum label, and append "Lede" if isLede is true
  const baseProminenceLabel = article.prominence
    ? getEnumLabel("PROMINENCE", article.prominence, dictionary)
    : "-";

  const prominenceLabel =
    article.isLede && baseProminenceLabel !== "-"
      ? `${baseProminenceLabel}, Lede`
      : article.isLede
        ? "Lede"
        : baseProminenceLabel;

  return {
    ...article,
    contentCategoryLabel,
    formattedDatePublished: formatDateToMMDDYYYY(article.datePublished),
    languageLabel: article.language
      ? getEnumLabel("LANGUAGE", article.language, dictionary)
      : "-",
    prominenceLabel,
    publisherCountryLabel: article.publisherCountry
      ? getEnumLabel("COUNTRY", article.publisherCountry, dictionary)
      : "-",
    publisherRegionLabel: article.publisherRegion
      ? getEnumLabel("REGION", article.publisherRegion, dictionary)
      : "-",
    publisherTierLabel: article.publisherTier
      ? getEnumLabel("TIER", article.publisherTier, dictionary)
      : "-",
    sentimentPolarityLabel: article.sentimentPolarity
      ? getEnumLabel("SENTIMENT", article.sentimentPolarity, dictionary)
      : "-",
  };
};

/**
 * Transforms a transformed article (with labels) to table row data format.
 *
 * This function is used in the data pipeline after {@link transformArticleWithLabels}
 * to convert articles into the column structure expected by AG Grid.
 *
 * The transformation:
 * - Maps enum labels to display values (e.g., "POSITIVE" → "Positive")
 * - Formats dates to MM/DD/YYYY format
 * - Joins multiple authors with commas
 * - Handles null/undefined values with appropriate fallbacks ("-" for strings, null for numbers)
 *
 * @param article - A transformed article with enum labels already applied
 * @returns Row data object matching the FeedArticleRowData type structure
 *
 * @example
 * ```ts
 * const transformed = transformArticleWithLabels(rawArticle, dictionary);
 * const rowData = transformArticleToRowData(transformed);
 * // rowData can now be passed directly to AG Grid
 * ```
 */
export const transformArticleToRowData = (
  article: TransformedFeedArticle,
): {
  DATE: string;
  PUBLICATION: string;
  HEADLINE: string;
  AUTHOR: string;
  IMPACT_SCORE: number | null;
  SENTIMENT: string;
  sentimentRationale: string | null;
  PROMINENCE: string;
  TIER: string;
  READERSHIP: number | null;
  DOMAIN_AUTHORITY: number | null;
  SOCIAL_ENGAGEMENTS: number | null;
  socialMetrics: SocialMetrics | null;
  CATEGORY: string;
  COUNTRY: string;
  REGION: string;
  LANGUAGE: string;
  NARRATIVE_CLUSTERS: string;
  URL: string;
  FIRST_MENTION: string | null;
  FIRST_MENTION_TERM: string | null;
} => {
  // Normalize socialMetrics to match SocialMetrics interface
  // Pass through the socialMetrics directly since it already matches the SocialMetrics interface
  const normalizedSocialMetrics: SocialMetrics | null =
    article.socialMetrics || null;

  return {
    DATE: article.formattedDatePublished ?? "-",
    PUBLICATION: article.publisher ?? "-",
    HEADLINE: article.headline ?? "-",
    AUTHOR:
      article.authors && article.authors.length > 0
        ? article.authors.join(", ")
        : "-",
    IMPACT_SCORE: article.impactScore ?? null,
    SENTIMENT: article.sentimentPolarityLabel ?? "-",
    sentimentRationale: article.sentimentRationale ?? null,
    PROMINENCE: article.prominenceLabel || "-",
    TIER: article.publisherTierLabel ?? "-",
    READERSHIP: article.publisherReadership ?? null,
    DOMAIN_AUTHORITY: article.publisherDomainAuthority ?? null,
    SOCIAL_ENGAGEMENTS: article.socialEngagement ?? null,
    socialMetrics: normalizedSocialMetrics,
    CATEGORY: article.contentCategoryLabel ?? "-",
    COUNTRY: article.publisherCountryLabel ?? "-",
    REGION: article.publisherRegionLabel ?? "-",
    LANGUAGE: article.languageLabel ?? "-",
    NARRATIVE_CLUSTERS: article.storyName ?? "-",
    URL: article.url ?? "-",
    FIRST_MENTION: article.firstMention?.snippet ?? null,
    FIRST_MENTION_TERM: article.firstMention?.term ?? null,
  };
};

// ============================================================================
// Narrative Clusters Table Helpers
// ============================================================================

/** Transforms a narrative cluster (Story) from the query into {@link NarrativeClusterRowData}. */
export const transformNarrativeClusterToRowData = (
  narrativeCluster: GetNarrativeClustersTableRow,
): NarrativeClusterRowData => {
  const stats = narrativeCluster.stats;
  return {
    id: narrativeCluster.id,
    TITLE: narrativeCluster.name ?? "",
    VOLUME: narrativeCluster.volume,
    AGGREGATED_IMPACT: narrativeCluster.aggregatedImpact,
    TOTAL_MENTIONS: (stats?.totalMentions ?? null) as number | string | null,
    NET_IMPACT_SCORE: stats?.netImpactScore ?? null,
    AVERAGE_ARTICLE_IMPACT: stats?.averageArticleImpact ?? null,
    AVERAGE_IMPACT_STRENGTH: stats?.averageImpactStrength ?? null,
    READERSHIP: (stats?.readership ?? null) as number | string | null,
    SOCIAL_ENGAGEMENTS: (stats?.socialEngagement ?? null) as
      | number
      | string
      | null,
    NET_SENTIMENT: stats?.netSentiment ?? null,
    GROSS_IMPACT: stats?.grossImpact ?? null,
    GROSS_POSITIVE_IMPACT: stats?.grossPositiveImpact ?? null,
    GROSS_NEUTRAL_IMPACT: stats?.grossNeutralImpact ?? null,
    GROSS_NEGATIVE_IMPACT: stats?.grossNegativeImpact ?? null,
    TIER_1: stats?.tier1 ?? null,
    TIER_2: stats?.tier2 ?? null,
    TIER_3: stats?.tier3 ?? null,
    HEADLINES: stats?.headlines ?? null,
    FEATURES: stats?.features ?? null,
    PASSING_MENTIONS: stats?.passingMentions ?? null,
  };
};
